import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { nanoid } from 'nanoid'
import { useSessionStore } from '@/stores/session'
import { useServerTimeStore } from '@/stores/serverTime'
import { useErrorCodesStore } from '@/stores/errorCodes'
import type { ApiResponse } from './types'
import { GlobalCode } from '@/enums/api'
import { get } from 'lodash'
import router from '@/router'
import { showToast } from 'vant'
import { toastify } from '@/utils'

const DEVICE_ID_KEY = 'Device-Id'

/**
 * 获取设备 ID
 * 格式：H5-{BrowserType}-{nanoid}
 * 生成后缓存到 localStorage，下次优先读取缓存
 */
function getDeviceId(): string {
  const cached = localStorage.getItem(DEVICE_ID_KEY)
  if (cached) return cached

  const ua = navigator.userAgent
  const platform = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'H5' : 'PC'
  let browser = 'Unknown'
  if (/CriOS/i.test(ua)) {
    browser = 'Chrome-iOS'
  } else if (/FxiOS/i.test(ua)) {
    browser = 'Firefox-iOS'
  } else if (/EdgA|EdgiOS/i.test(ua)) {
    browser = 'Edge'
  } else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) {
    browser = 'Chrome'
  } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
    browser = 'Safari'
  } else if (/Firefox/i.test(ua)) {
    browser = 'Firefox'
  } else if (/Edg/i.test(ua)) {
    browser = 'Edge'
  }

  const deviceId = `${platform}-${browser}-${nanoid()}`
  localStorage.setItem(DEVICE_ID_KEY, deviceId)
  return deviceId
}

/**
 * Axios 实例配置
 */
const instance: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * 请求拦截器
 */
instance.interceptors.request.use(
  (config) => {
    // 自动添加 Device ID
    config.headers['Device-Id'] = getDeviceId()

    // 自动添加 Session ID
    const sessionStore = useSessionStore()
    const sessionId = sessionStore.getSessionId()

    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * 响应拦截器
 */
instance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { code, message, data, server_ts } = response.data

    // 从响应头提取 Session ID（登录时）
    const sessionId = response.headers['x-session-id']
    if (sessionId) {
      const sessionStore = useSessionStore()
      sessionStore.setSessionId(sessionId)
    }

    // 更新服务器时间戳
    if (server_ts) {
      const serverTimeStore = useServerTimeStore()
      serverTimeStore.updateServerTime(server_ts)
    }

    // 外层 code 不为 0 表示请求失败
    if (code !== GlobalCode.Success) {
      // 401 未授权，清除 session 并跳转登录，并记录当前页供登录后跳回
      if (code == GlobalCode.Unauthorized) {
        const sessionStore = useSessionStore()
        // 只有登录过期才提示，未登录直接访问接口不提示
        if (sessionStore.sessionId) {
          toastify({ message: '登录已过期，请重新登录', type: 'danger' })
        }
        sessionStore.clearSession()
        const current = router.currentRoute.value
        // 不在登录/注册页时用当前页作为 redirect；已在登录/注册页时保留已有 redirect（避免多请求同时 401 时被覆盖）
        const redirect =
          current.path !== '/login' && current.path !== '/register'
            ? current.fullPath
            : typeof current.query.redirect === 'string' &&
                current.query.redirect.startsWith('/') &&
                !current.query.redirect.startsWith('//')
              ? current.query.redirect
              : undefined
        router.push({
          path: '/login',
          query: redirect ? { redirect } : {},
        })
      }

      // 605 系统维护中，跳转到维护页面
      if (code == GlobalCode.ServerMaintenance) {
        router.push({
          path: '/500',
          query: {
            title: data?.title,
            start_at: data?.start_at,
            end_at: data?.end_at,
          },
        })
      }

      // 使用错误码字典获取提示语
      const errorCodesStore = useErrorCodesStore()
      const errorMessage = errorCodesStore.getErrorMessage(code) || message || '请求失败'

      // 修改响应数据中的 message 为字典中的提示语
      response.data.message = errorMessage

      return Promise.reject(response.data)
    }

    return response
  },
  (error) => {
    // 处理 HTTP 错误
    if (error.response) {
      const { status, data } = error.response

      // 401 未授权，清除 session 并跳转登录，并记录当前页供登录后跳回
      if (status === 401) {
        showToast({ message: '登录已过期，请重新登录', type: 'fail' })
        const sessionStore = useSessionStore()
        sessionStore.clearSession()
        const current = router.currentRoute.value
        // 不在登录/注册页时用当前页作为 redirect；已在登录/注册页时保留已有 redirect（避免多请求同时 401 时被覆盖）
        const redirect =
          current.path !== '/login' && current.path !== '/register'
            ? current.fullPath
            : typeof current.query.redirect === 'string' &&
                current.query.redirect.startsWith('/') &&
                !current.query.redirect.startsWith('//')
              ? current.query.redirect
              : undefined
        router.push({
          path: '/login',
          query: redirect ? { redirect } : {},
        })
      }

      // 使用服务器返回的错误信息
      const message = data?.message || `请求失败 (${status})`
      return Promise.reject(new Error(message))
    }

    // 网络错误或超时
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new Error('请求超时，请重试'))
    }

    return Promise.reject(new Error(error.message || '网络错误'))
  },
)

export default {
  /**
   * 封装 GET 请求
   */
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return instance.get<ApiResponse<T>>(url, config).then((res) => res.data)
  },

  /**
   * 封装 POST 请求
   */
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return instance.post<ApiResponse<T>>(url, data, config).then((res) => res.data)
  },

  /**
   * 封装 PUT 请求
   */
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return instance.put<ApiResponse<T>>(url, data, config).then((res) => res.data)
  },

  /**
   * 封装 DELETE 请求
   */
  del<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return instance.delete<ApiResponse<T>>(url, config).then((res) => res.data)
  },
}
